import { useMemo, useState } from 'react';
import { CircularProgress, ToggleButton, ToggleButtonGroup } from '@mui/material';
import { faChevronDown, faChevronUp } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';

import Utils from '../../../../../utils/immersive_utils';
import { IPortfolioPerformancePosition } from '../../../../../providers/portfolio-performance/portfolio-performance.types';
import { classifyInstrument, PositionType } from './classify-position';
import { buildPositionGroups, PositionGroup } from './build-position-groups';
import './styles.scss';

type PositionsCardProps = {
  positions: IPortfolioPerformancePosition[];
  totalPv: number | null;
  denomination: string | null | undefined;
  isLoading: boolean;
};

type TypeFilter = 'all' | PositionType;

const TYPE_FILTERS: { value: TypeFilter; label: string }[] = [
  { value: 'all', label: 'All' },
  { value: 'token', label: 'Token' },
  { value: 'futures', label: 'Futures' },
  { value: 'options', label: 'Options' },
];

const fmtSigned = (value: number, precision: number, negativeClass: string): JSX.Element => (
  <span className={value < 0 ? negativeClass : undefined}>
    {Utils.formatNumber(value, precision)}
  </span>
);

// Token/spot rows use the account's own denomination precision (2dp
// USD-like, 4dp otherwise — see the `precision` prop below); futures and
// options are always 2dp regardless of denomination — 4dp on a position
// size like 188,850.0000 contracts or -0.3000 options is just noise.
const rowPrecision = (isToken: boolean, basePrecision: number): number =>
  isToken ? basePrecision : 2;

const GroupRow = ({
  group,
  depth,
  precision,
  collapsed,
  onToggle,
}: {
  group: PositionGroup;
  depth: number;
  precision: number;
  collapsed: boolean;
  onToggle: () => void;
}) => {
  const groupPrecision = rowPrecision(group.key === 'token', precision);
  return (
    <div
      className="portfolio-performance-positions-row portfolio-performance-positions-group-row"
      style={{ paddingLeft: 12 + depth * 20 }}
      onClick={onToggle}
    >
      <span className="portfolio-performance-positions-cell-instrument">
        <FontAwesomeIcon
          icon={collapsed ? faChevronDown : faChevronUp}
          className="portfolio-performance-positions-chevron"
        />
        <span>
          {group.label} ({group.rows.length + (group.children?.length ?? 0)})
        </span>
      </span>
      <span className="portfolio-performance-positions-cell">
        {fmtSigned(group.totalPosition, groupPrecision, 'portfolio-performance-positions-negative')}
      </span>
      <span className="portfolio-performance-positions-cell" />
      <span className="portfolio-performance-positions-cell" />
      <span className="portfolio-performance-positions-cell">
        {fmtSigned(group.totalPv, groupPrecision, 'portfolio-performance-positions-negative')}
      </span>
    </div>
  );
};

const PositionRow = ({
  position,
  depth,
  precision,
}: {
  position: IPortfolioPerformancePosition;
  depth: number;
  precision: number;
}) => {
  const positionPrecision = rowPrecision(
    classifyInstrument(position.instrument) === 'token',
    precision,
  );
  return (
    <div className="portfolio-performance-positions-row" style={{ paddingLeft: 12 + depth * 20 }}>
      <span className="portfolio-performance-positions-cell-instrument">
        <span>{position.instrument}</span>
      </span>
      <span className="portfolio-performance-positions-cell">
        {fmtSigned(
          position.position,
          positionPrecision,
          'portfolio-performance-positions-negative',
        )}
      </span>
      <span className="portfolio-performance-positions-cell">
        {position.averagePrice === null ? '-' : Utils.formatNumber(position.averagePrice, 2)}
      </span>
      <span className="portfolio-performance-positions-cell">
        {position.markPrice === null ? '-' : Utils.formatNumber(position.markPrice, 2)}
      </span>
      <span className="portfolio-performance-positions-cell">
        {fmtSigned(position.pv, positionPrecision, 'portfolio-performance-positions-negative')}
      </span>
    </div>
  );
};

export const PositionsCard = ({
  positions,
  totalPv,
  denomination,
  isLoading,
}: PositionsCardProps) => {
  // Same convention as the account card's "Capital" tile — 2dp for
  // USD-like denominations, 4dp otherwise (BTC/crypto-denominated accounts).
  const precision = denomination === 'USD' || denomination === 'USDC' ? 2 : 4;

  const [typeFilter, setTypeFilter] = useState<TypeFilter>('all');
  const [grouped, setGrouped] = useState(true);
  const [collapsedGroups, setCollapsedGroups] = useState<Set<string>>(new Set());

  const filteredPositions = useMemo(
    () =>
      typeFilter === 'all'
        ? positions
        : positions.filter((p) => classifyInstrument(p.instrument) === typeFilter),
    [positions, typeFilter],
  );

  const groups = useMemo(
    () => (grouped ? buildPositionGroups(filteredPositions) : []),
    [grouped, filteredPositions],
  );

  const toggleGroup = (key: string) => {
    setCollapsedGroups((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  };

  if (isLoading) {
    return (
      <div className="loading-positions-table">
        <CircularProgress size={28} />
      </div>
    );
  }

  const hasPositions = filteredPositions.length > 0;

  return (
    <div className="portfolio-performance-positions-table-container">
      <div className="portfolio-performance-positions-table-content-header">
        <span>Positions</span>
        {totalPv !== null && (
          <span className="portfolio-performance-positions-total">
            Total PV: {Utils.formatNumber(totalPv, precision)}
          </span>
        )}
      </div>

      <div className="portfolio-performance-positions-controls">
        <ToggleButtonGroup
          size="small"
          exclusive
          value={typeFilter}
          onChange={(_, value) => value && setTypeFilter(value)}
          className="portfolio-performance-positions-toggle-group"
        >
          {TYPE_FILTERS.map(({ value, label }) => (
            <ToggleButton key={value} value={value}>
              {label}
            </ToggleButton>
          ))}
        </ToggleButtonGroup>

        <ToggleButtonGroup
          size="small"
          exclusive
          value={grouped ? 'grouped' : 'flat'}
          onChange={(_, value) => value && setGrouped(value === 'grouped')}
          className="portfolio-performance-positions-toggle-group"
        >
          <ToggleButton value="flat">No grouping</ToggleButton>
          <ToggleButton value="grouped">Group</ToggleButton>
        </ToggleButtonGroup>
      </div>

      {hasPositions ? (
        <div className="portfolio-performance-positions-grid-wrapper">
          <div className="portfolio-performance-positions-row portfolio-performance-positions-header-row">
            <span className="portfolio-performance-positions-cell-instrument">Instrument</span>
            <span className="portfolio-performance-positions-cell">Position</span>
            <span className="portfolio-performance-positions-cell">Avg Price</span>
            <span className="portfolio-performance-positions-cell">Mark Price</span>
            <span className="portfolio-performance-positions-cell">PV</span>
          </div>

          {grouped
            ? groups.map((group) => (
                <div key={group.key}>
                  <GroupRow
                    group={group}
                    depth={0}
                    precision={precision}
                    collapsed={collapsedGroups.has(group.key)}
                    onToggle={() => toggleGroup(group.key)}
                  />
                  {!collapsedGroups.has(group.key) &&
                    (group.children
                      ? group.children.map((child) => (
                          <div key={child.key}>
                            <GroupRow
                              group={child}
                              depth={1}
                              precision={precision}
                              collapsed={collapsedGroups.has(child.key)}
                              onToggle={() => toggleGroup(child.key)}
                            />
                            {!collapsedGroups.has(child.key) &&
                              child.rows.map((p) => (
                                <PositionRow
                                  key={p.instrument}
                                  position={p}
                                  depth={2}
                                  precision={precision}
                                />
                              ))}
                          </div>
                        ))
                      : group.rows.map((p) => (
                          <PositionRow
                            key={p.instrument}
                            position={p}
                            depth={1}
                            precision={precision}
                          />
                        )))}
                </div>
              ))
            : filteredPositions.map((p) => (
                <PositionRow key={p.instrument} position={p} depth={0} precision={precision} />
              ))}
        </div>
      ) : (
        <div className="portfolio-performance-positions-empty">No open positions</div>
      )}
    </div>
  );
};
